// TEMP(user-type-switch): 스테이징 권한 시험용 임시 카드 — 한 계정으로 수요자 · 공급자 · PwC 내부 권한을 시험해요.
// 시험이 끝나면 이 파일과 my-profile.tsx의 연결 두 줄(import · <TempUserTypeSwitch />)을 지워요. 서버는 backend/app/api/v1/endpoints/temp_user_type_switch.py.
import { useState } from "react";

import { navigatorClient } from "@/api";
import { NvAlertDialog, NvButton } from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { getApiError } from "@/lib/api-error";
import type { ApiResponse } from "@/types";

import { useMyPage } from "./my-page-shared";

const TYPES = [
  { value: "demand", label: "수요자" },
  { value: "supplier", label: "공급자" },
  { value: "internal", label: "PwC 내부" },
] as const;

type UserType = (typeof TYPES)[number]["value"];

async function switchUserType(userType: UserType) {
  const { data } = await navigatorClient.patch<ApiResponse<{ user_type: string }>>("/me/test-user-type", { user_type: userType });
  return data.data!;
}

export function TempUserTypeSwitch() {
  const { summary, refresh } = useMyPage();
  const { refreshUser } = useAuth();
  const current = summary.profile.user_type;
  const [busy, setBusy] = useState<UserType | null>(null);
  const [alert, setAlert] = useState<string | null>(null);

  const change = async (next: UserType) => {
    setBusy(next);
    try {
      await switchUserType(next);
      // 등급은 요청마다 서버가 계산해요 — 로그인 정보(GNB · 라우트 가드)와 내 정보 요약을 다시 읽으면 바로 바뀌어요
      await refreshUser();
      refresh();
    } catch (err) {
      setAlert(getApiError(err)?.message ?? "회원 유형을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-5 flex flex-col gap-4 rounded-nv-card border border-dashed border-nv-border bg-nv-surface p-7">
      <div className="flex items-center gap-2">
        <h2 className="text-[17px] font-medium leading-5 text-nv-text-primary">회원 유형 전환</h2>
        <span className="rounded-nv-chip bg-nv-bg-page px-2 py-[2px] text-[11px] font-bold leading-[13px] text-nv-text-secondary">테스트용 · 임시</span>
      </div>
      <p className="text-[13px] leading-[18px] text-nv-text-secondary">
        스테이징에서 회원 유형별 권한을 시험하려고 잠시 둔 기능이에요. 바꾸면 바로 적용되고, 시험이 끝나면 이 카드는 없어져요.
      </p>
      <div className="flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <NvButton
            key={t.value}
            variant={t.value === current ? "primary" : "secondary"}
            size="sm"
            disabled={busy !== null || t.value === current}
            onClick={() => change(t.value)}
          >
            {t.label}
            {t.value === current ? " (현재)" : ""}
          </NvButton>
        ))}
      </div>
      <ul className="flex flex-col gap-[6px] rounded-nv-card bg-nv-bg-page p-4">
        {[
          "다시 로그인할 때: PwC 내부는 [PwC SSO 로그인]으로만, 수요자 · 공급자는 [Outlook 로그인]으로만 들어갈 수 있어요. 이미 로그인한 상태에서는 상관없어요.",
          "PwC 메일 계정이 아니면 PwC 내부로 바꾸지 마세요. PwC SSO로 로그인할 수 없어 다시 들어올 수 없어요.",
          "내부 전용 에이전트를 북마크 · 비교해 둔 계정은 수요자 · 공급자일 때 목록과 건수에서 빠졌다가, PwC 내부로 돌아오면 다시 보여요.",
        ].map((line) => (
          <li key={line} className="text-[13px] leading-[18px] text-nv-text-secondary">
            · {line}
          </li>
        ))}
      </ul>
      <NvAlertDialog open={alert !== null} onOpenChange={(o) => !o && setAlert(null)} message={alert ?? ""} actions={<NvButton onClick={() => setAlert(null)}>확인</NvButton>} />
    </div>
  );
}
